/**
 * DatePicker — 캘린더 날짜 선택기
 *
 * 커스텀 캘린더 팝오버로 날짜를 선택한다.
 * 외부 라이브러리 없이 순수 구현.
 */

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { cn } from './cn'
import { CalendarCore } from './CalendarCore'
import styles from './DatePicker.module.css'

export interface DatePickerProps {
  /** 선택된 날짜 (YYYY-MM-DD 문자열 또는 Date) */
  value?: string | Date | null
  /** 날짜 변경 콜백 */
  onChange?: (date: Date | null, formatted: string) => void
  /** 필드 레이블 */
  label?: string
  /** 에러 메시지 */
  error?: string
  /** 보조 안내 텍스트 */
  helperText?: string
  /** 플레이스홀더 */
  placeholder?: string
  /** 날짜 포맷 (표시용). 기본 'yyyy년 M월 d일' */
  displayFormat?: (date: Date) => string
  /** 최소 선택 가능 날짜 */
  minDate?: Date
  /** 최대 선택 가능 날짜 */
  maxDate?: Date
  /** 입력 크기 */
  size?: 'sm' | 'md'
  /** 비활성 상태 */
  disabled?: boolean
  /** 캘린더를 absolute 팝오버 대신 in-flow로 렌더 — 부모가 overflow:hidden일 때 사용 */
  inline?: boolean
  /**
   * 캘린더 팝오버를 document.body에 portal로 렌더하고 트리거 위치에 fixed로 띄운다.
   * 부모가 overflow:hidden(모달·카드)이라 팝오버가 잘리지만, 레이아웃은 늘리고 싶지 않을 때 사용.
   * inline 과 함께 지정하면 inline 이 우선.
   */
  popoverPortal?: boolean
  /**
   * 선택 단위. 기본 'day'.
   *   - 'day': 날짜 선택 → onChange(date, 'YYYY-MM-DD')
   *   - 'month': 연-월 선택 → onChange(그 달 1일 date, 'YYYY-MM'). value 도 'YYYY-MM' 문자열
   */
  mode?: 'day' | 'month'
  /** 추가 클래스명 */
  className?: string
}

/** 'YYYY-MM' 문자열/Date를 그 달 1일 Date로 파싱 */
export function toMonth(v: string | Date | null | undefined): Date | null {
  if (!v) return null
  if (v instanceof Date) return v
  const m = /^(\d{4})-(\d{2})/.exec(v)
  if (!m) return null
  return new Date(Number(m[1]), Number(m[2]) - 1, 1)
}

export function formatMonthISO(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

function defaultMonthFormat(d: Date): string {
  return `${d.getFullYear()}년 ${d.getMonth() + 1}월`
}

export function toDate(v: string | Date | null | undefined): Date | null {
  if (!v) return null
  if (v instanceof Date) return v
  const d = new Date(v + 'T00:00:00')
  return isNaN(d.getTime()) ? null : d
}

/** datetime 문자열/Date를 파싱 (시간 포함) */
export function toDateTime(v: string | Date | null | undefined): Date | null {
  if (!v) return null
  if (v instanceof Date) return v
  const d = new Date(v)
  return isNaN(d.getTime()) ? null : d
}

export function formatISO(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function defaultFormat(d: Date): string {
  return `${d.getFullYear()}년 ${d.getMonth() + 1}월 ${d.getDate()}일`
}

export function isSameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
}

export function getDaysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate()
}

export function getCalendarDays(year: number, month: number): { date: Date; currentMonth: boolean }[] {
  const firstDay = new Date(year, month, 1).getDay()
  const daysInMonth = getDaysInMonth(year, month)
  const daysInPrevMonth = month === 0 ? getDaysInMonth(year - 1, 11) : getDaysInMonth(year, month - 1)

  const days: { date: Date; currentMonth: boolean }[] = []

  // 이전 달 날짜
  for (let i = firstDay - 1; i >= 0; i--) {
    const d = daysInPrevMonth - i
    const prevMonth = month === 0 ? 11 : month - 1
    const prevYear = month === 0 ? year - 1 : year
    days.push({ date: new Date(prevYear, prevMonth, d), currentMonth: false })
  }

  // 현재 달 날짜
  for (let d = 1; d <= daysInMonth; d++) {
    days.push({ date: new Date(year, month, d), currentMonth: true })
  }

  // 다음 달 날짜 (6주 = 42일 채우기)
  const remaining = 42 - days.length
  for (let d = 1; d <= remaining; d++) {
    const nextMonth = month === 11 ? 0 : month + 1
    const nextYear = month === 11 ? year + 1 : year
    days.push({ date: new Date(nextYear, nextMonth, d), currentMonth: false })
  }

  return days
}

export function DatePicker({
  value,
  onChange,
  label,
  error,
  helperText,
  placeholder,
  displayFormat,
  minDate,
  maxDate,
  size = 'md',
  disabled = false,
  inline = false,
  popoverPortal = false,
  mode = 'day',
  className,
}: DatePickerProps) {
  const isMonthMode = mode === 'month'
  const resolvedPlaceholder = placeholder ?? (isMonthMode ? '월을 선택하세요' : '날짜를 선택하세요')
  const resolvedFormat = displayFormat ?? (isMonthMode ? defaultMonthFormat : defaultFormat)
  const [open, setOpen] = useState(false)
  // selected 는 매 렌더 새 Date 객체 — 시간값(timestamp)으로 메모이즈해 안정적 참조를 유지한다.
  const selectedTime = (isMonthMode ? toMonth(value) : toDate(value))?.getTime() ?? null
  const selected = useMemo(() => (selectedTime != null ? new Date(selectedTime) : null), [selectedTime])

  const wrapperRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const popoverRef = useRef<HTMLDivElement>(null)
  // portal 팝오버 위치 (트리거 기준 fixed). inline 이 아니고 popoverPortal 일 때만 사용.
  const usePortal = popoverPortal && !inline
  const [popPos, setPopPos] = useState<{ top: number; left: number } | null>(null)

  const handleSelectDay = useCallback((d: Date) => {
    onChange?.(d, formatISO(d))
    setOpen(false)
  }, [onChange])

  const handleSelectMonth = useCallback((year: number, month: number) => {
    const d = new Date(year, month, 1)
    onChange?.(d, formatMonthISO(d))
    setOpen(false)
  }, [onChange])

  const handleToggle = useCallback(() => {
    if (!disabled) setOpen((v) => !v)
  }, [disabled])

  // portal 팝오버 위치 계산 — 열릴 때 + 스크롤/리사이즈 시 트리거 아래로 재배치
  useLayoutEffect(() => {
    if (!open || !usePortal) return
    const update = () => {
      const rect = triggerRef.current?.getBoundingClientRect()
      if (rect) setPopPos({ top: rect.bottom + 4, left: rect.left })
    }
    update()
    window.addEventListener('scroll', update, true)
    window.addEventListener('resize', update)
    return () => {
      window.removeEventListener('scroll', update, true)
      window.removeEventListener('resize', update)
    }
  }, [open, usePortal])

  // 외부 클릭 시 닫기 (portal 팝오버는 wrapper 밖이므로 별도 예외 처리)
  useEffect(() => {
    if (!open) return
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node
      if (wrapperRef.current?.contains(target)) return
      if (popoverRef.current?.contains(target)) return
      setOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [open])

  // ESC 키로 닫기
  useEffect(() => {
    if (!open) return
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('keydown', handleKey)
    return () => document.removeEventListener('keydown', handleKey)
  }, [open])

  return (
    <div ref={wrapperRef} className={cn(styles.wrapper, className)}>
      {label && <label className={styles.label}>{label}</label>}

      <button
        ref={triggerRef}
        type="button"
        className={cn(
          styles.trigger,
          styles[size],
          disabled && styles.disabled,
          error && styles.triggerError,
        )}
        onClick={handleToggle}
        disabled={disabled}
      >
        <span className={cn(styles.triggerText, !selected && styles.placeholder)}>
          {selected ? resolvedFormat(selected) : resolvedPlaceholder}
        </span>
        <svg className={styles.calendarIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <rect x="3" y="4" width="18" height="18" rx="2" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M16 2v4M8 2v4M3 10h18" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open && !usePortal && (
        <div className={cn(styles.popover, inline && styles.popoverInline)}>
          <CalendarCore
            mode="single"
            monthMode={isMonthMode}
            selected={selected}
            focusDate={selected}
            minDate={minDate}
            maxDate={maxDate}
            onSelectDay={handleSelectDay}
            onSelectMonth={handleSelectMonth}
          />
        </div>
      )}

      {/* portal 팝오버 — 모달/카드 overflow 밖(body)에 fixed로 띄워 잘림 방지 */}
      {open && usePortal && popPos &&
        createPortal(
          <div
            ref={popoverRef}
            className={cn(styles.popover, styles.popoverPortal)}
            style={{ top: popPos.top, left: popPos.left }}
          >
            <CalendarCore
              mode="single"
              monthMode={isMonthMode}
              selected={selected}
              focusDate={selected}
              minDate={minDate}
              maxDate={maxDate}
              onSelectDay={handleSelectDay}
              onSelectMonth={handleSelectMonth}
            />
          </div>,
          document.body,
        )}

      {error && <span className={styles.errorText}>{error}</span>}
      {!error && helperText && <span className={styles.helperText}>{helperText}</span>}
    </div>
  )
}
